import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  chakra,
  Divider,
  Flex,
  Heading,
  Text,
  useToast
} from "@chakra-ui/react";
import { FormInput } from "../../../../../components/Forms";
import { useForm } from "react-hook-form";
import { showErrorToast, showSuccessToast, translateSubElementString } from "../../../../../utils";
import { useQueryClient } from "react-query";
import {
  reportKeys,
  useExportScopeOneAndTwoTotalData,
  useUpdateTurnover
} from "../../../../../hooks/query/reports";
import { Card } from "../../../../../components/Card";
import * as React from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { RatiosSection, ScopeSection, TotalsSection } from "./components";

function ScopeOneAndTwoReport({ reportId, data, year, quarter, scopeThreeTotalCo2Emissions }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    isSubmitting
  } = useForm({
    defaultValues: { turnover: data.turnover }
  });

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Turnover Updated!");
      queryClient.invalidateQueries([...reportKeys.reportData(), year, quarter]);
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const { mutate: updateTurnover, isLoading: isLoadingOnUpdate } = useUpdateTurnover(queryConfig);

  const onSubmit = (data) => {
    updateTurnover({ reportId, turnover: data.turnover });
  };

  const {
    mutate: exportTotalData
  } = useExportScopeOneAndTwoTotalData({
    onError: err => {
      const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
      showErrorToast(toast, parsedErrorObj?.message ?? err.message);
      dispatch(hideAppSpinner());
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleExportClick = () => {
    dispatch(showAppSpinner());
    exportTotalData({ params: { year, quarter } });
  };

  return (
    <Box>
      <Card boxShadow="xs">
        <Flex justifyContent={"space-between"}>
          <Box>
            <Heading size="md" marginBlock="1" inlineSize="250px">
              {t("turnoverBox.title")}
            </Heading>
            <Text size="sm" color="esg_gray.300">
              {t("turnoverBox.subTitle")}
            </Text>
          </Box>
          <Box>
            <Button
              colorScheme="customScheme"
              width="42"
              title={t("buttons.scope1&2ExportTooltip")}
              onClick={handleExportClick}
            >
              {t("buttons.exportScope1&2")}
            </Button>
          </Box>
        </Flex>
        <Divider marginBlock={5} />
        <chakra.form id="turnoverForm" onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="end" gap="4">
            <Flex alignItems="end" gap="2">
              <FormInput
                name="turnover"
                label={t("metrics.turnover")}
                errors={errors}
                {...register("turnover")}
              />
              <Text display="flex"
                    justifyContent="center"
                    alignItems="center"
                    marginBlockStart="6"
                    fontWeight="bold">£m</Text>
            </Flex>
            <Button
              colorScheme="customScheme"
              type="submit"
              width="32"
              isLoading={isSubmitting || isLoadingOnUpdate}
            >
              Recalculate
            </Button>
          </Flex>
        </chakra.form>
      </Card>

      {/* Scope 1: Direct Emissions Section */}
      <ScopeSection
        title={t("scope1Title")}
        data={data.directEmissions}
        totalEntity={{
          title: translateSubElementString("reportsPage.directTotal"),
          subTitle: ` (${globalTranslation(`measures.tonnes`)})`,
          value: data.directTotalCo2Emissions
        }}
      />

      {/* Scope 2: Indirect Emissions Section */}
      <ScopeSection
        title={t("scope2Title")}
        data={data.indirectEmissions}
      />

      <TotalsSection data={data} scopeThreeTotalCo2Emissions={scopeThreeTotalCo2Emissions} />
      <RatiosSection data={data} />
    </Box>
  );
}

export default ScopeOneAndTwoReport;